import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  TrendingUp, Zap, Shield, Activity, Wallet,
  ArrowUpRight, ArrowDownRight, RefreshCw, Gauge, User
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { fortress } from '../lib/fortress-sdk.js'

// Simulated portfolio data
const portfolioData = [
  { date: 'Jun 14', value: 12400 },
  { date: 'Jun 15', value: 12800 },
  { date: 'Jun 16', value: 13100 },
  { date: 'Jun 17', value: 12900 },
  { date: 'Jun 18', value: 13400 },
  { date: 'Jun 19', value: 13700 },
  { date: 'Jun 20', value: 14000 },
  { date: 'Jun 21', value: 14231 },
]

// Simulated arbitrage events
const initialArbEvents = [
  { id: 1, dex: 'Uniswap V3', pair: 'FORT/USDC', profit: 4.32, direction: 'BUY', timestamp: '2 min ago' },
  { id: 2, dex: 'SushiSwap', pair: 'FORT/ETH', profit: 2.18, direction: 'SELL', timestamp: '8 min ago' },
  { id: 3, dex: 'Uniswap V3', pair: 'FORT/USDC', profit: 6.71, direction: 'BUY', timestamp: '15 min ago' },
  { id: 4, dex: 'SushiSwap', pair: 'FORT/ETH', profit: 1.95, direction: 'BUY', timestamp: '22 min ago' },
  { id: 5, dex: 'Uniswap V3', pair: 'FORT/USDC', profit: 3.44, direction: 'SELL', timestamp: '30 min ago' },
]

// Simulated sentiment history
const sentimentHistory = [
  { time: '00:00', score: 0.2 },
  { time: '04:00', score: 0.5 },
  { time: '08:00', score: 0.8 },
  { time: '12:00', score: 0.6 },
  { time: '16:00', score: 0.4 },
  { time: '20:00', score: 0.65 },
]

export default function Dashboard() {
  const [puterUser, setPuterUser] = useState(null)
  const [fortBalance, setFortBalance] = useState(0)
  const [arbEvents, setArbEvents] = useState(initialArbEvents)
  const [sentiment, setSentiment] = useState(0.65)
  const [isLive, setIsLive] = useState(true)
  const [sdkError, setSdkError] = useState('')

  // Load Puter user on mount
  useEffect(() => {
    (async () => {
      try {
        const user = await fortress.auth.getUser();
        setPuterUser(user);
        if (user) {
          const bal = await fortress.wallet.getBalance();
          setFortBalance(bal);
        }
      } catch (err) {
        setSdkError(err.message);
      }
    })();
  }, []);

  // Simulate live arb events
  useEffect(() => {
    if (!isLive) return
    const interval = setInterval(() => {
      const newEvent = {
        id: Date.now(),
        dex: Math.random() > 0.5 ? 'Uniswap V3' : 'SushiSwap',
        pair: Math.random() > 0.5 ? 'FORT/USDC' : 'FORT/ETH',
        profit: +(Math.random() * 8 + 1).toFixed(2),
        direction: Math.random() > 0.5 ? 'BUY' : 'SELL',
        timestamp: 'Just now'
      }
      setArbEvents(prev => [newEvent, ...prev.slice(0, 9)])
    }, 12000)
    return () => clearInterval(interval)
  }, [isLive])

  // Simulate sentiment fluctuations
  useEffect(() => {
    const interval = setInterval(() => {
      setSentiment(prev => {
        const change = (Math.random() - 0.5) * 0.1
        return Math.max(-1, Math.min(1, +(prev + change).toFixed(2)))
      })
    }, 8000)
    return () => clearInterval(interval)
  }, [])

  const getSentimentLabel = (score) => {
    if (score > 0.5) return { text: 'Bullish', color: 'text-green-400' }
    if (score > 0) return { text: 'Slightly Bullish', color: 'text-green-300' }
    if (score > -0.5) return { text: 'Slightly Bearish', color: 'text-red-300' }
    return { text: 'Bearish', color: 'text-red-400' }
  }

  const sentimentInfo = getSentimentLabel(sentiment)
  const gaugeRotation = sentiment * 90 // -90 to +90 degrees
  const totalProfit = arbEvents.reduce((sum, e) => sum + e.profit, 0)

  return (
    <div className="min-h-screen bg-fortress-dark">
      {/* Top Navigation */}
      <header className="border-b border-fortress-gold/20 bg-fortress-indigo/50 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl text-fortress-gold">🏰 FortressNation</span>
            <span className="text-xs px-2 py-1 rounded-full bg-fortress-gold/10 text-fortress-gold">Atrium v4.2</span>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 text-sm ${isLive ? 'text-green-400' : 'text-gray-400'}`}>
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
              {isLive ? 'Live' : 'Paused'}
            </div>
            <button
              onClick={() => setIsLive(!isLive)}
              className="px-3 py-1 text-sm border border-fortress-gold/30 rounded-md hover:bg-fortress-gold/10 transition"
            >
              {isLive ? 'Pause' : 'Resume'}
            </button>

            {/* Puter Auth — replaces old wallet address */}
            {puterUser ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-300 flex items-center gap-1">
                  <User size={14} className="text-fortress-gold" />
                  {puterUser.username}
                </span>
                <span className="text-xs font-mono text-fortress-gold">{fortBalance} FORT</span>
                <button
                  onClick={async () => {
                    await fortress.auth.signOut();
                    setPuterUser(null);
                    setFortBalance(0);
                  }}
                  className="px-2 py-1 text-xs border border-fortress-gold/30 rounded-md hover:bg-fortress-gold/10 transition text-gray-400"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={async () => {
                  try {
                    const user = await fortress.auth.signIn();
                    setPuterUser(user);
                    const bal = await fortress.wallet.getBalance();
                    setFortBalance(bal);
                    fortress.thread.emit('userSignedIn', user);
                  } catch (err) {
                    setSdkError(err.message);
                  }
                }}
                className="px-4 py-2 bg-fortress-gold text-fortress-dark rounded-md text-sm font-medium hover:bg-fortress-gold/90 transition"
              >
                Sign in with Puter
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Dashboard Grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Top Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-xl bg-fortress-card border border-fortress-gold/20"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-400">Portfolio Value</span>
              <Wallet className="text-fortress-gold" size={18} />
            </div>
            <p className="text-3xl font-mono font-bold">$14,231</p>
            <p className="text-sm text-green-400 mt-2 flex items-center gap-1">
              <ArrowUpRight size={14} /> +12.4% this month
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 rounded-xl bg-fortress-card border border-fortress-gold/20"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-400">Total Arb Profit</span>
              <Zap className="text-fortress-gold" size={18} />
            </div>
            <p className="text-3xl font-mono font-bold">${totalProfit.toFixed(2)}</p>
            <p className="text-sm text-gray-400 mt-2">{arbEvents.length} trades today</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 rounded-xl bg-fortress-card border border-fortress-gold/20"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-400">Active Strategies</span>
              <Activity className="text-fortress-gold" size={18} />
            </div>
            <p className="text-3xl font-mono font-bold">7</p>
            <p className="text-sm text-green-400 mt-2">All healthy</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 rounded-xl bg-fortress-card border border-fortress-gold/20"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-400">Yield APY</span>
              <TrendingUp className="text-fortress-gold" size={18} />
            </div>
            <p className="text-3xl font-mono font-bold">8.7%</p>
            <p className="text-sm text-green-400 mt-2">Aave USDC pool</p>
          </motion.div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Portfolio Chart */}
          <div className="lg:col-span-2 p-6 rounded-xl bg-fortress-card border border-fortress-gold/20">
            <h3 className="font-display text-lg mb-4">Portfolio Growth</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={portfolioData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e1b4b',
                    border: '1px solid #f59e0b',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: '#f59e0b' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Sentiment Gauge */}
          <div className="p-6 rounded-xl bg-fortress-card border border-fortress-gold/20 flex flex-col items-center justify-center">
            <h3 className="font-display text-lg mb-4 w-full">Market Sentiment</h3>
            <div className="relative w-48 h-24 mb-4 overflow-hidden">
              {/* Gauge background arc */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full border-[16px] border-transparent"
                style={{
                  background: 'conic-gradient(from 180deg, #ef4444 0deg, #f59e0b 45deg, #22c55e 90deg, #f59e0b 135deg, #ef4444 180deg)',
                  mask: 'radial-gradient(circle at center, transparent 55%, black 55%)',
                  WebkitMask: 'radial-gradient(circle at center, transparent 55%, black 55%)'
                }}
              />
              {/* Needle */}
              <motion.div
                className="absolute bottom-0 left-1/2 w-1 h-20 bg-white origin-bottom rounded-full"
                animate={{ rotate: gaugeRotation }}
                transition={{ type: 'spring', stiffness: 100, damping: 15 }}
                style={{ transformOrigin: 'bottom center' }}
              />
              {/* Center dot */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rounded-full shadow-lg" />
            </div>
            <p className={`text-2xl font-mono font-bold ${sentimentInfo.color}`}>
              {sentiment > 0 ? '+' : ''}{sentiment.toFixed(2)}
            </p>
            <p className={`text-sm ${sentimentInfo.color}`}>{sentimentInfo.text}</p>
            <p className="text-xs text-gray-400 mt-2">Powered by DeepSeek</p>
          </div>
        </div>

        {/* Live Arbitrage Feed */}
        <div className="p-6 rounded-xl bg-fortress-card border border-fortress-gold/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg">Live Arbitrage Feed</h3>
            <RefreshCw className={`text-gray-400 ${isLive ? 'animate-spin' : ''}`} size={16} />
          </div>
          <div className="space-y-2">
            {arbEvents.map((event, i) => (
              <motion.div
                key={event.id}
                initial={i === 0 ? { opacity: 0, x: -20 } : false}
                animate={i === 0 ? { opacity: 1, x: 0 } : false}
                className="flex items-center justify-between p-3 rounded-lg bg-fortress-dark/50 border border-fortress-gold/10"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded ${event.direction === 'BUY' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    {event.direction === 'BUY' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{event.dex}</p>
                    <p className="text-xs text-gray-400">{event.pair}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono font-bold text-green-400">+${event.profit.toFixed(2)}</p>
                  <p className="text-xs text-gray-400">{event.timestamp}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}